/**
 * AgentEventsProvider.tsx — Workspace agent SSE events provider with intent_signal parsing.
 *
 * CR-WS-019 | TASK-UI-WS019-002, TASK-UI-WS019-008
 */

import type {
  ReactNode} from "react";
import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback
} from "react";
import { predictIntentLocally } from "@/shared/hooks/useIntentSignal";
import type { WorkspaceTask } from "@/shared/data/workspaces";

export interface IntentSignal {
  intent: string;
  confidence: number;
  mode?: string;
  allScores?: Record<string, number>;
  method?: "regex" | "llm" | "hybrid";
  taskId?: string;
  timestamp: string;
}

/** Event types multiplexed over the one workspace SSE stream (CE-001 §2.5). */
export const WORKSPACE_BUS_EVENTS = [
  "vfs.node.changed",
  "context.tree.updated",
  "context.file.updated",
  "vfs.node.access_state",
  "cua.confirm_request",
  "cua.space.created",
  "cua.space.destroyed",
  "agent.cua.loop.detected",
] as const;


export type WorkspaceBusEventType = (typeof WORKSPACE_BUS_EVENTS)[number];
export type WorkspaceEventHandler = (payload: unknown) => void;
/** "open" once connected; "closed" while (re)connecting after an error. */
export type WorkspaceStreamState = "connecting" | "open" | "closed";

import {
  AgentEventsContext,
  useAgentEvents,
  type AgentEventsContextValue,
} from "@/app/providers/AgentEventsContext";
import { useAgentSessionStore } from "@/features/agent-session/store/agentSessionStore";

export { AgentEventsContext, useAgentEvents, type AgentEventsContextValue };

export function isRecentSignal(
  signal: IntentSignal | null,
  windowMs = 30000
): boolean {
  if (!signal) return false;
  return Date.now() - new Date(signal.timestamp).getTime() < windowMs;
}

export function hasRunningTask(tasks: WorkspaceTask[] = []): boolean {
  return tasks.some((t) => t.status === "running");
}

export function AgentEventsProvider({
  workspaceId,
  children,
}: {
  workspaceId: string;
  children: ReactNode;
}) {
  const [intentSignal, setIntentSignal] = useState<IntentSignal | null>(null);
  const esRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearIntentTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listenersRef = useRef(new Map<string, Set<WorkspaceEventHandler>>());
  const [streamState, setStreamState] = useState<WorkspaceStreamState>("connecting");

  const subscribe = useCallback(
    (type: WorkspaceBusEventType, handler: WorkspaceEventHandler) => {
      const map = listenersRef.current;
      if (!map.has(type)) map.set(type, new Set());
      map.get(type)!.add(handler);
      return () => {
        map.get(type)?.delete(handler);
      };
    },
    []
  );

  useEffect(() => {
    if (!workspaceId) return;
    if (typeof EventSource === "undefined") {
      setStreamState("closed");
      return;
    }

    const connect = () => {
      esRef.current?.close();
      const es = new EventSource(`/api/v1/workspaces/${workspaceId}/events/stream`);
      esRef.current = es;
      es.onopen = () => setStreamState("open");

      // Multi-channel bus: fan each typed event out to its subscribers.
      for (const type of WORKSPACE_BUS_EVENTS) {
        es.addEventListener(type, (e: MessageEvent) => {
          let payload: unknown;
          try {
            payload = JSON.parse(e.data);
          } catch {
            return; // ignore malformed frames
          }
          listenersRef.current.get(type)?.forEach((h) => {
            try {
              h(payload);
            } catch {
              /* one bad subscriber must not break the others */
            }
          });
        });
      }

      es.addEventListener("intent_signal", (e: MessageEvent) => {
        try {
          const raw = JSON.parse(e.data);
          const signal: IntentSignal = {
            intent: raw.intent,
            confidence: raw.confidence ?? 1,
            mode: raw.mode,
            allScores: raw.allScores,
            method: raw.method ?? "llm",
            taskId: raw.taskId,
            timestamp: raw.timestamp || new Date().toISOString(),
          };
          setIntentSignal(signal);

          // Auto clear after 5s
          if (clearIntentTimeoutRef.current) {
            clearTimeout(clearIntentTimeoutRef.current);
          }
          clearIntentTimeoutRef.current = setTimeout(() => {
            setIntentSignal(null);
          }, 5000);
        } catch {
          // Ignore parse errors
        }
      });

      es.onerror = () => {
        setStreamState("closed");
        es.close();
        reconnectTimeoutRef.current = setTimeout(connect, 3000);
      };
    };

    connect();

    return () => {
      esRef.current?.close();
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (clearIntentTimeoutRef.current) clearTimeout(clearIntentTimeoutRef.current);
    };
  }, [workspaceId]);

  const checkRecent = useCallback(
    (signal: IntentSignal | null, windowMs = 30000) => isRecentSignal(signal, windowMs),
    []
  );

  const checkRunning = useCallback(
    (tasks: WorkspaceTask[]) => hasRunningTask(tasks),
    []
  );

  const isAgentThinking =
    intentSignal !== null && isRecentSignal(intentSignal, 5000);

  const activeSessionsCount = useAgentSessionStore((s) =>
    Object.values(s.sessions).filter((sess) => sess.status === 'running').length
  );
  const pendingApprovalCount = useAgentSessionStore(
    (s) => Object.keys(s.pendingApprovals).length
  );
  const isBudgetExceeded = useAgentSessionStore((s) => s.isBudgetExceeded);

  const workspaceEventStream =
    streamState === "open"
      ? "connected"
      : streamState === "connecting"
      ? "disconnected"
      : "error";

  return (
    <AgentEventsContext.Provider
      value={{
        subscribe,
        streamState,
        intentSignal,
        isAgentThinking,
        hasRunningTask: checkRunning,
        isRecentSignal: checkRecent,
        predictIntentLocally,
        activeSessionsCount,
        pendingApprovalCount,
        isBudgetExceeded,
        workspaceEventStream,
      }}
    >
      {children}
    </AgentEventsContext.Provider>
  );
}

/**
 * Subscribe to a workspace bus event. `handler` may change identity every
 * render; the latest one is always called.
 */
export function useWorkspaceEvents(type: WorkspaceBusEventType, handler: WorkspaceEventHandler): void {
  const { subscribe } = useContext(AgentEventsContext);
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => subscribe(type, (p) => ref.current(p)), [subscribe, type]);
}

export const useWorkspaceStreamState = (): WorkspaceStreamState => useContext(AgentEventsContext).streamState;
export default AgentEventsProvider;
